import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { paymentStatus, accountPurchases, API } from "@/lib/api";
import { Overline, PrimaryButton, GhostButton } from "@/components/site/Primitives";
import { AuthForm } from "@/components/site/AuthForm";
import { CheckCircle2, Download, BookOpen } from "lucide-react";

export default function Account() {
  const { user, ready, logout, refresh } = useAuth();
  const loc = useLocation();
  const sessionId = new URLSearchParams(loc.search).get("session_id");
  const [confirming, setConfirming] = useState(!!sessionId);
  const [library, setLibrary] = useState(null);

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    const poll = async () => {
      try {
        const res = await paymentStatus(sessionId);
        if (res.payment_status === "paid") { await refresh(); setConfirming(false); return; }
      } catch { /* retry */ }
      if (tries++ < 8) setTimeout(poll, 2000); else setConfirming(false);
    };
    poll();
  }, [sessionId, refresh]);

  useEffect(() => {
    if (!user || confirming) return;
    accountPurchases().then((d) => setLibrary(d.items || [])).catch(() => setLibrary([]));
  }, [user, confirming]);

  if (!ready) return <div className="min-h-[60vh] flex items-center justify-center font-serif text-2xl text-brand-muted">One moment…</div>;

  if (!user) {
    return (
      <div className="min-h-[70vh] flex items-center" data-testid="account-page">
        <section className="max-w-md mx-auto px-6 py-24 w-full">
          <div className="text-center mb-8"><Overline>Your account</Overline><h1 className="text-4xl font-light mt-3">Sign in to Rosewood Cottage</h1></div>
          <AuthForm />
        </section>
      </div>
    );
  }

  return (
    <div data-testid="account-page">
      <section className="max-w-2xl mx-auto px-6 py-24 text-center">
        {confirming ? (
          <p className="font-serif text-3xl text-brand-muted">Confirming your membership…</p>
        ) : (
          <>
            <Overline>Your account</Overline>
            <h1 className="text-5xl font-light mt-4 mb-3 leading-tight">Hello, {user.name}</h1>
            <p className="font-body text-brand-muted mb-10">{user.email}</p>
            <div className="bg-brand-warm-white border border-brand-linen p-10 text-left">
              {user.is_subscribed ? (
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="text-brand-sage mt-1 shrink-0" size={24} />
                  <div>
                    <h2 className="font-serif text-2xl">You're a Cottage Member</h2>
                    <p className="font-body text-brand-body mt-2">Every daily recipe and Journal letter is yours. Thank you for supporting Agnes's work.</p>
                    <div className="mt-6"><PrimaryButton to="/daily-recipe" testid="account-recipe-btn">Today's recipe</PrimaryButton></div>
                  </div>
                </div>
              ) : (
                <div>
                  <h2 className="font-serif text-2xl mb-2">You're not a member yet</h2>
                  <p className="font-body text-brand-body mb-6">Join the Cottage Circle for a daily recipe and every letter from Agnes.</p>
                  <PrimaryButton to="/membership" testid="account-join-btn">Become a member · $9/month</PrimaryButton>
                </div>
              )}
            </div>

            {/* Your Library — purchased digital books */}
            <div className="mt-12 text-left" data-testid="account-library">
              <div className="flex items-center gap-3 mb-5">
                <BookOpen size={20} className="text-brand-copper" />
                <h2 className="font-serif text-2xl">Your Library</h2>
              </div>
              {library === null ? (
                <p className="font-body text-brand-muted">Gathering your books…</p>
              ) : library.length === 0 ? (
                <div className="bg-brand-warm-white border border-brand-linen p-8">
                  <p className="font-body text-brand-body">You haven't purchased any books yet.</p>
                  <div className="mt-5"><PrimaryButton to="/library" testid="account-browse-btn">Browse the Library</PrimaryButton></div>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4" data-testid="account-library-list">
                  {library.map((b) => (
                    <div key={b.slug} className="flex gap-4 bg-brand-warm-white border border-brand-linen p-4" data-testid={`library-item-${b.slug}`}>
                      {b.cover && <img src={b.cover} alt={b.title} className="w-16 h-24 object-cover shrink-0" />}
                      <div className="flex flex-col min-w-0">
                        <h3 className="font-serif text-lg leading-snug">{b.title}</h3>
                        <a href={`${API}/download/${b.session_id}?slug=${b.slug}`} data-testid={`library-download-${b.slug}`}
                          className="mt-auto inline-flex items-center gap-2 font-sans text-[0.66rem] uppercase tracking-[0.14em] text-brand-copper link-underline">
                          <Download size={14} /> Download PDF
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button onClick={logout} data-testid="account-logout" className="mt-12 link-underline font-sans text-xs uppercase tracking-[0.16em] text-brand-copper">Sign out</button>
          </>
        )}
      </section>
    </div>
  );
}
